import _extends from '@babel/runtime/helpers/esm/extends';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import { useRef, useState, useLayoutEffect, useMemo } from 'react';
import { Button } from '@paypalcorp/pp-react-buttons';
import { IconButton } from '@paypalcorp/pp-react-icon-button';
import { CloseIcon } from '@paypalcorp/pp-react-icons';
import ReactDOM from 'react-dom';
import { css, useTheme, jsx } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { math } from 'polished';
import { textStyles } from '@paypalcorp/pp-react-text';
import { utilStyles, ZINDEX_COACHTIP, uniqueId } from '@paypalcorp/pp-react-utils';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8;

function styles(tokens) {
  var sysSpaceXl = tokens.sysSpaceXl,
      sysSizePointerWidthLg = tokens.sysSizePointerWidthLg;

  var _utilStyles = utilStyles(tokens),
      caretStyles = _utilStyles.caretStyles;

  var coachtipCaretVerticalOffset = math("".concat(sysSizePointerWidthLg, "/2"));
  var positionTopLeft = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: position_top_left;\n    ", ";\n    ", ";\n  "])), caretStyles.positionTop(coachtipCaretVerticalOffset), caretStyles.positionShiftLeft(sysSpaceXl));
  var positionTopCenter = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: position_top_center;\n    ", ";\n    ", ";\n  "])), caretStyles.positionTop(coachtipCaretVerticalOffset), caretStyles.positionCenter(sysSizePointerWidthLg));
  var positionTopRight = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: position_top_right;\n    ", ";\n    ", ";\n  "])), caretStyles.positionTop(coachtipCaretVerticalOffset), caretStyles.positionShiftRight(sysSpaceXl));
  var positionLeft = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: position_left;\n    ", ";\n  "])), caretStyles.positionLeft(coachtipCaretVerticalOffset));
  var positionRight = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: position_right;\n    ", ";\n  "])), caretStyles.positionRight(coachtipCaretVerticalOffset));
  var positionBottomLeft = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: position_bottom_left;\n    ", ";\n    ", ";\n  "])), caretStyles.positionBottom(coachtipCaretVerticalOffset), caretStyles.positionShiftLeft(sysSpaceXl));
  var positionBottomCenter = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: position_bottom_center;\n    ", ";\n    ", ";\n  "])), caretStyles.positionBottom(coachtipCaretVerticalOffset), caretStyles.positionCenter(sysSizePointerWidthLg));
  var positionBottomRight = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: position_bottom_right;\n    ", ";\n    ", ";\n  "])), caretStyles.positionBottom(coachtipCaretVerticalOffset), caretStyles.positionShiftRight(sysSpaceXl));
  return {
    positionCoachTip: function positionCoachTip(position) {
      return {
        'top-left': positionTopLeft,
        'top-center': positionTopCenter,
        'top-right': positionTopRight,
        left: positionLeft,
        right: positionRight,
        'bottom-left': positionBottomLeft,
        'bottom-center': positionBottomCenter,
        'bottom-right': positionBottomRight
      }[position];
    }
  };
}

var _templateObject$1, _templateObject2$1, _templateObject3$1, _templateObject4$1, _templateObject5$1, _templateObject6$1, _templateObject7$1, _templateObject8$1, _templateObject9, _templateObject10;

function styles$1(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    console.warn('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysBorderRadiusSm = tokens.sysBorderRadiusSm,
      sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
      sysSpaceMd = tokens.sysSpaceMd,
      sysSpaceXl = tokens.sysSpaceXl,
      sysSpaceXs = tokens.sysSpaceXs,
      sysSizeIconXs = tokens.sysSizeIconXs,
      sysSizePointerWidthLg = tokens.sysSizePointerWidthLg,
      sysSizeTapTargetSm = tokens.sysSizeTapTargetSm;

  var _utilStyles = utilStyles(tokens),
      caretStyles = _utilStyles.caretStyles,
      elevationStyles = _utilStyles.elevationStyles;

  var _textStyles = textStyles(tokens),
      textBody = _textStyles.body,
      textCaption = _textStyles.caption,
      title = _textStyles.title;

  var _caretPosStyles = styles(tokens),
      positionCoachTip = _caretPosStyles.positionCoachTip; // Calc variables:


  var coachtipHeaderCloseBtnMarginCalc = math("(".concat(sysSizeTapTargetSm, " - ").concat(sysSizeIconXs, ")/2"));
  var coachtipHeaderCloseBtnSpacingCalc = math("(".concat(coachtipHeaderCloseBtnMarginCalc, " + ").concat(sysSizeIconXs, ")/2"));
  var base = css(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n    label: coach-tip_base;\n    display: none;\n    position: absolute;\n    background-color: ", ";\n    max-width: ", ";\n    padding: ", ";\n    border-radius: ", ";\n    z-index: ", ";\n    ", ";\n    ", "\n    &:focus {\n      outline: none;\n    }\n  "])), sysColorBackgroundSurface, '20rem', sysSpaceXl, sysBorderRadiusSm, ZINDEX_COACHTIP, caretStyles.base(sysSizePointerWidthLg), elevationStyles.level3);
  var header = css(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteral(["\n    label: header;\n    display: flex;\n    width: 100%;\n    justify-content: space-between;\n  "])));
  var headerText = css(_templateObject3$1 || (_templateObject3$1 = _taggedTemplateLiteral(["\n    label: header_text;\n    ", ";\n    display: inline-block;\n    margin-bottom: ", ";\n    max-width: calc(100% - ", ");\n  "])), title(), sysSpaceMd, coachtipHeaderCloseBtnSpacingCalc);
  var noHeaderContentText = css(_templateObject4$1 || (_templateObject4$1 = _taggedTemplateLiteral(["\n    label: no_header;\n    display: inline-block;\n    max-width: calc(100% - ", ");\n  "])), coachtipHeaderCloseBtnSpacingCalc);
  var footer = css(_templateObject5$1 || (_templateObject5$1 = _taggedTemplateLiteral(["\n    label: footer;\n    display: flex;\n    align-items: center;\n    margin-top: ", ";\n  "])), sysSpaceMd);
  var footerLabel = css(_templateObject6$1 || (_templateObject6$1 = _taggedTemplateLiteral(["\n    label: footer_label;\n    ", ";\n    display: inline-block;\n    flex: 1 1 auto;\n  "])), textCaption());
  var footerButtonContainer = css(_templateObject7$1 || (_templateObject7$1 = _taggedTemplateLiteral(["\n    label: footer_button_container;\n    display: inline-block;\n  "])));
  var footerButton = css(_templateObject8$1 || (_templateObject8$1 = _taggedTemplateLiteral(["\n    label: footer_button;\n    margin-left: ", ";\n    [dir='rtl'] & {\n      margin-left: 0;\n      margin-right: ", ";\n    }\n  "])), sysSpaceXl, sysSpaceXl);
  var contentText = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: text;\n    ", ";\n  "])), textBody());
  var closeButton = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: close_button;\n    margin-top: -", ";\n    margin-right: -", ";\n    padding: ", ";\n    z-index: 1;\n    [dir='rtl'] & {\n      margin-right: 0;\n      margin-left: -", ";\n    }\n  "])), sysSpaceMd, sysSpaceMd, sysSpaceXs, sysSpaceMd);
  return {
    base: base,
    header: header,
    headerText: headerText,
    contentText: contentText,
    noHeaderContentText: noHeaderContentText,
    footer: footer,
    footerLabel: footerLabel,
    footerButtonContainer: footerButtonContainer,
    footerButton: footerButton,
    closeButton: closeButton,
    positionCoachTip: positionCoachTip
  };
}

var _excluded = ["id", "headerText", "text", "caretPos", "prevButtonText", "nextButtonText", "onPrevButtonClick", "onNextButtonClick", "paginationText", "targetElementId", "targetElementRef", "containerRef", "isOpen", "onClose", "className", "closeButtonLabel", "headerTextClassName", "contentTextClassName", "paginationTextClassName", "prevButtonClassName", "nextButtonClassName"];

var getRefCurrent = function getRefCurrent(myref) {
  return (
    /* istanbul ignore next */
    myref && myref.current ? myref.current : null
  );
};

var CoachTip = function CoachTip(_ref) {
  var _styles$positionCoach;

  var _ref$id = _ref.id,
      id = _ref$id === void 0 ? uniqueId('CoachTip_') : _ref$id,
      headerText = _ref.headerText,
      _ref$text = _ref.text,
      text = _ref$text === void 0 ? 'default' : _ref$text,
      _ref$caretPos = _ref.caretPos,
      caretPos = _ref$caretPos === void 0 ? 'bottom-left' : _ref$caretPos,
      prevButtonText = _ref.prevButtonText,
      nextButtonText = _ref.nextButtonText,
      onPrevButtonClick = _ref.onPrevButtonClick,
      onNextButtonClick = _ref.onNextButtonClick,
      paginationText = _ref.paginationText,
      targetElementId = _ref.targetElementId,
      targetElementRef = _ref.targetElementRef,
      containerRef = _ref.containerRef,
      _ref$isOpen = _ref.isOpen,
      isOpen = _ref$isOpen === void 0 ? true : _ref$isOpen,
      onClose = _ref.onClose,
      className = _ref.className,
      _ref$closeButtonLabel = _ref.closeButtonLabel,
      closeButtonLabel = _ref$closeButtonLabel === void 0 ? 'close coach tip' : _ref$closeButtonLabel,
      headerTextClassName = _ref.headerTextClassName,
      contentTextClassName = _ref.contentTextClassName,
      paginationTextClassName = _ref.paginationTextClassName,
      prevButtonClassName = _ref.prevButtonClassName,
      nextButtonClassName = _ref.nextButtonClassName,
      rest = _objectWithoutProperties(_ref, _excluded);
  /* istanbul ignore next */


  if (!targetElementId) {
    console.warn('Required prop targetElementId missing.');
  }

  var coachTipRef = useRef();
  var focusableEls = document.querySelectorAll('a, button, textarea, input, select');
  var initialWidth = useRef();
  var caretWidth;
  var caretHeight;
  var rootFontSize;
  var caretOffset;
  var caretSpaceHorizontal;

  var _useState = useState(isOpen),
      _useState2 = _slicedToArray(_useState, 2),
      show = _useState2[0],
      setShow = _useState2[1];
  /* istanbul ignore next */


  var getCaretDimensions = function getCaretDimensions() {
    var coachTip = getRefCurrent(coachTipRef);

    if (coachTip) {
      var rootFontSizePx = window && window.getComputedStyle(document.body).getPropertyValue('font-size') || '16px';
      rootFontSize = parseInt(rootFontSizePx.split('px')[0], 10);
      caretWidth = 0.9375 * rootFontSize; // 15

      caretHeight = 0.5625 * rootFontSize; // 9

      caretOffset = 0.25 * rootFontSize; // 4 between caret and tooltip icon

      caretSpaceHorizontal = 1.5 * rootFontSize; // 24 from caret to tooltip edge
    }
  };
  /* istanbul ignore next */


  var getWindowBoundary = function getWindowBoundary() {
    var coachTipWindow = getRefCurrent(containerRef);

    if (coachTipWindow) {
      var pos = coachTipWindow.getBoundingClientRect();
      return {
        windowEl: coachTipWindow,
        top: pos.top,
        right: pos.right,
        bottom: pos.bottom,
        left: pos.left
      };
    }

    return {
      windowEl: window,
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
      left: 0
    };
  };

  var
  /* istanbul ignore next */
  positionSelf = function positionSelf(coachTip, targetEl, width, height) {
    if (coachTip && targetEl) {
      var targetElWidth = targetEl.offsetWidth;
      var targetElHeight = targetEl.offsetHeight; // start from targetEl position then move accordingly

      var scrollX = window.scrollX || window.pageXOffset;
      var scrollY = window.scrollY || window.pageYOffset;
      var offsetLeft = targetEl.getBoundingClientRect().left + scrollX;
      var offsetTop = targetEl.getBoundingClientRect().top + scrollY;
      var spacingOffset = caretSpaceHorizontal + caretWidth / 2;
      var moveDown = offsetTop + targetElHeight + caretHeight + caretOffset;
      var moveUp = offsetTop - height - caretHeight - caretOffset;
      var shiftLeft = offsetLeft - spacingOffset + targetElWidth / 2;
      var shiftRight = offsetLeft - width + spacingOffset + targetElWidth / 2;
      var shiftCenter = offsetLeft - width / 2 + targetElWidth / 2;
      /* istanbul ignore next */

      switch (caretPos) {
        case 'top-left':
          offsetLeft = shiftLeft;
          offsetTop = moveDown;
          break;

        case 'top-center':
          offsetLeft = shiftCenter;
          offsetTop = moveDown;
          break;

        case 'top-right':
          offsetLeft = shiftRight;
          offsetTop = moveDown;
          break;

        case 'left':
          offsetLeft = offsetLeft + targetElWidth + caretWidth + caretOffset;
          offsetTop = offsetTop - height / 2 + targetElHeight / 2;
          break;

        case 'right':
          offsetLeft = offsetLeft - width - caretWidth - caretOffset;
          offsetTop = offsetTop - height / 2 + targetElHeight / 2;
          break;

        case 'bottom-right':
          offsetLeft = shiftRight;
          offsetTop = moveUp;
          break;

        case 'bottom-center':
          offsetLeft = shiftCenter;
          offsetTop = moveUp;
          break;

        default:
          // 'bottom-left':
          offsetLeft = shiftLeft;
          offsetTop = moveUp;
      }

      var _containerElm = getRefCurrent(containerRef);

      if (_containerElm) {
        var containerPos = _containerElm.getBoundingClientRect();

        var scrollTop = _containerElm.scrollTop;
        var scrollLeft = _containerElm.scrollLeft;
        offsetTop = offsetTop - containerPos.top + scrollTop - scrollY;
        offsetLeft = offsetLeft - containerPos.left + scrollLeft - scrollX;
      }

      coachTip.style.top = "".concat(offsetTop, "px");
      coachTip.style.left = "".concat(offsetLeft, "px");
    }
  }; // Coach Tip can be narrower than the max width depending on where it is positioned,
  // (e.g. if position close to right of window it will be narrow and tall, therefore,
  // its width must determined before positioning since Coach Tip's width is used in
  // positioning calcs.

  /* istanbul ignore next */


  var refreshCoachtip = function refreshCoachtip() {
    var coachTip = getRefCurrent(coachTipRef);
    var targetEl = targetElementRef ? getRefCurrent(targetElementRef) : document.getElementById(targetElementId);

    if (coachTip && targetEl) {
      var coachTipMaxWidthPx = getComputedStyle(coachTip, 'style').maxWidth;
      var coachTipMaxWidth = parseInt(coachTipMaxWidthPx.split('px')[0], 10) || 320;
      var coachTipWindowPos = getWindowBoundary();
      var targetLeft = targetEl.getBoundingClientRect().left;
      var targetWidth = targetEl.offsetWidth;
      var caretCenterToCorner = caretSpaceHorizontal + caretWidth / 2; // Update display property here instead of css to
      // get coach tip height/width to avoid page scroll

      coachTip.style.display = 'inline-block';
      var availableSpace;
      var maxWidth;
      /* istanbul ignore next */

      switch (caretPos) {
        case 'top-left':
        case 'bottom-left':
          availableSpace = coachTipWindowPos.right - targetLeft - targetWidth / 2;
          maxWidth = availableSpace + caretCenterToCorner;
          break;

        case 'top-right':
        case 'bottom-right':
          availableSpace = targetLeft + targetWidth / 2;
          maxWidth = availableSpace + caretCenterToCorner;
          break;

        case 'top-center':
        case 'bottom-center':
          // find shorter side
          var availableLeftSpace = targetLeft + targetWidth / 2;
          var availableRightSpace = coachTipWindowPos.right - targetLeft - targetWidth / 2;
          availableSpace = availableLeftSpace > availableRightSpace ? availableRightSpace : availableLeftSpace;
          maxWidth = availableSpace * 2;
          break;

        case 'right':
          availableSpace = targetLeft;
          maxWidth = availableSpace - caretOffset - caretWidth;
          break;

        case 'left':
          availableSpace = coachTipWindowPos.right - targetLeft - targetWidth;
          maxWidth = availableSpace - caretOffset - caretWidth;
          break;

        default:
          maxWidth = coachTipMaxWidth;
      }

      var width = maxWidth < initialWidth.current ? maxWidth : initialWidth.current;
      var height = coachTip.offsetHeight;
      positionSelf(coachTip, targetEl, width, height);
    }
  };

  var initializeCoachTip = function initializeCoachTip() {
    var coachTip = getRefCurrent(coachTipRef);
    /* istanbul ignore next */

    if (coachTip) {
      initialWidth.current = coachTip.getBoundingClientRect().width || 320;
      getCaretDimensions();
      coachTip.focus();
    }
  };

  useLayoutEffect(function () {
    initializeCoachTip();
    window.addEventListener('resize', refreshCoachtip);
    return function () {
      window.removeEventListener('resize', refreshCoachtip);

      if (focusableEls.length > 0) {
        var initFocus = focusableEls[0];
        initFocus.focus();
      }
    };
  }, []);
  useLayoutEffect(function () {
    setShow(isOpen);

    if (isOpen) {
      refreshCoachtip();
    }

    return function () {};
  }, [isOpen, headerText, text, nextButtonText, prevButtonText, paginationText]);
  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles$1(tokens);
  }, [tokens]);

  var coachTipCssVal = [_styles.base, (_styles$positionCoach = _styles.positionCoachTip) === null || _styles$positionCoach === void 0 ? void 0 : _styles$positionCoach.call(_styles, caretPos)];
  var closeButton = jsx("span", {
    css: _styles.closeButton,
    "data-ppui": "true"
  }, jsx(IconButton, _extends({}, {
    buttonType: 'tertiary',
    size: 'sm',
    svgIconComponent: CloseIcon
  }, {
    onClick: function handleClose(e) {
      if (onClose) {
        onClose(e);
      } else {
        setShow(false);
      }
      /* istanbul ignore next */


      if (focusableEls.length > 0) {
        var nextFocus = focusableEls[0];
        nextFocus.focus();
      }
    },
    "data-ppui": "true"
  }), closeButtonLabel));
  var footerButtons = jsx("div", {
    css: _styles.footerButtonContainer,
    "data-ppui": "true"
  }, prevButtonText && jsx(Button, {
    tertiary: true,
    css: _styles.footerButton,
    className: prevButtonClassName,
    onClick: onPrevButtonClick,
    "data-ppui": "true"
  }, prevButtonText), nextButtonText && jsx(Button, {
    tertiary: true,
    css: _styles.footerButton,
    className: nextButtonClassName,
    onClick: onNextButtonClick,
    "data-ppui": "true"
  }, nextButtonText));
  var label = jsx("div", {
    css: _styles.footerLabel,
    className: paginationTextClassName,
    "data-ppui": "true"
  }, paginationText);
  var coachTipElement = jsx("div", _extends({
    css: coachTipCssVal,
    className: className,
    id: id,
    ref: coachTipRef,
    role: "dialog",
    "aria-hidden": !show,
    tabIndex: 0,
    "data-ppui-info": "coach-tip_2.6.12"
  }, rest), function header() {
    if (headerText) {
      return jsx("div", {
        css: _styles.header,
        "data-ppui": "true"
      }, jsx("div", {
        css: _styles.headerText,
        className: headerTextClassName,
        "data-ppui": "true"
      }, headerText), closeButton);
    }

    return jsx("div", {
      css: _styles.header,
      "data-ppui": "true"
    }, jsx("div", {
      css: [_styles.contentText, _styles.noHeaderContentText],
      className: contentTextClassName,
      "data-ppui": "true"
    }, text), closeButton);
  }(), headerText && jsx("div", {
    css: _styles.contentText,
    className: contentTextClassName,
    "data-ppui": "true"
  }, text), (paginationText || prevButtonText || nextButtonText) && jsx("div", {
    css: _styles.footer,
    "data-ppui": "true"
  }, paginationText && label, footerButtons));
  var containerElm = containerRef && containerRef.current;
  return show && ReactDOM.createPortal(coachTipElement, containerElm || document.body);
};

export { CoachTip, styles$1 as coachTipStyles };